import { FUNCTIONS_URL } from "@/lib/backend";
import React, { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  PaymentRequestButtonElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { Button } from "@/components/ui/button";
import type { PaymentRequest } from "@stripe/stripe-js";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";


const CheckoutForm = ({ clientSecret }: { clientSecret: string }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [isPaymentElementReady, setIsPaymentElementReady] = useState(false);
  const [prAvailable, setPrAvailable] = useState(false);
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) {
      setMessage("Stripe not properly initialized.");
      return;
    }
    if (!isPaymentElementReady) {
      setMessage("PaymentElement not ready yet. Please wait.");
      return;
    }

    setLoading(true);
    setMessage("");

    const result = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.origin + "/stripe-test",
      },
      redirect: "always",
    });

    if (result.error) {
      setMessage(result.error.message || "Unexpected error");
    } else {
      setMessage("Payment successful!");
    }

    setLoading(false);
  };

  useEffect(() => {
    if (!stripe || !clientSecret) return;

    const pr = stripe.paymentRequest({
      country: "US",
      currency: "usd",
      total: { label: "Test Payment", amount: 500 },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    pr.canMakePayment().then((result) => {
      if (result) {
        setPrAvailable(true);
        setPaymentRequest(pr);
      } else {
        setPrAvailable(false);
        setPaymentRequest(null);
      }
    });

    pr.on("paymentmethod", async (ev) => {
      setLoading(true);
      const { error: confirmError } = await stripe.confirmCardPayment(
        clientSecret,
        { payment_method: ev.paymentMethod.id },
        { handleActions: false }
      );

      if (confirmError) {
        ev.complete("fail");
        setMessage(confirmError.message || "Payment failed");
        setLoading(false);
        return;
      }

      ev.complete("success");
      const finalResult = await stripe.confirmCardPayment(clientSecret);
      if (finalResult.error) {
        setMessage(finalResult.error.message || "Payment failed");
      } else {
        setMessage("Payment successful!");
      }
      setLoading(false);
    });
  }, [stripe, clientSecret]);

  // Check if PaymentElement is available
  const paymentElement = elements?.getElement(PaymentElement);

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-6 max-w-md mx-auto">
      <div className="text-sm space-y-1 mb-4 p-3 bg-gray-100 rounded">
        <div>Stripe initialized: {stripe ? "✓ Yes" : "✗ No"}</div>
        <div>Elements initialized: {elements ? "✓ Yes" : "✗ No"}</div>
        <div>Express wallets available (Apple/Google): {prAvailable ? "✓ Yes" : "✗ No"}</div>
        <div>PaymentElement available: {paymentElement ? "✓ Yes" : "✗ No"}</div>
        <div>PaymentElement ready: {isPaymentElementReady ? "✓ Yes" : "✗ No"}</div>
        {(!stripe || !elements) && <div className="text-red-600 font-medium">Error: Stripe not properly initialized</div>}
        {!isPaymentElementReady && <div className="text-red-600 font-medium">Warning: PaymentElement is not ready yet.</div>}
      </div>

      {paymentRequest && prAvailable && (
        <div className="mb-2">
          <PaymentRequestButtonElement
            options={{
              paymentRequest,
              style: { paymentRequestButton: { type: "buy", theme: "dark", height: "44px" } },
            }}
          />
        </div>
      )}

      <PaymentElement
        onReady={() => {
          setIsPaymentElementReady(true);
        }}
      />

      <Button
        type="submit"
        disabled={!stripe || !elements || !isPaymentElementReady || loading}
        className="w-full"
      >
        {loading ? "Processing..." : "Pay"}
      </Button>
      {message && <div className="text-sm mt-2">{message}</div>}
    </form>
  );
};

export default function StripeTest() {
  const { user } = useAuth();
  const { isAdmin, loading: roleLoading } = useUserRole();
  const [clientSecret, setClientSecret] = useState("");
  const [error, setError] = useState("");
  const [stripePromise, setStripePromise] = useState<Promise<import("@stripe/stripe-js").Stripe | null> | null>(null);
  const [stripeKeyError, setStripeKeyError] = useState("");
useEffect(() => {
  document.title = "Stripe Test | PaymentElement readiness";
  const descContent = "Confirm Stripe PaymentElement mounts and submits without issues.";
  let meta = document.querySelector('meta[name="description"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'description');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', descContent);

  // Add noindex for safety
  let robots = document.querySelector('meta[name="robots"]');
  if (!robots) {
    robots = document.createElement('meta');
    robots.setAttribute('name', 'robots');
    document.head.appendChild(robots);
  }
  robots.setAttribute('content', 'noindex, nofollow');

  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  link.href = window.location.origin + '/stripe-test';
}, []);
useEffect(() => {
  const fetchPublishableKey = async () => {
    try {
      const res = await fetch(`${FUNCTIONS_URL}/get-stripe-config`);
      const data = await res.json();
      if (res.ok && data.publishableKey) {
        const promise = loadStripe(data.publishableKey);
        setStripePromise(promise);
      } else {
        setStripeKeyError(data.error || "Failed to fetch Stripe publishable key");
      }
    } catch (err) {
      setStripeKeyError("Network error fetching Stripe publishable key");
    }
  };
  // Only fetch key when user is admin (to avoid hinting this page is available)
  if (user && isAdmin) fetchPublishableKey();
}, [user, isAdmin]);
useEffect(() => {
  const fetchSecret = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("create-test-intent", {
        body: {},
      });
      if (error) {
        setError(typeof error === 'string' ? error : 'Failed to fetch clientSecret');
        return;
      }
      if (data?.clientSecret) {
        setClientSecret(data.clientSecret);
      } else {
        setError("Failed to fetch clientSecret");
      }
    } catch (err) {
      setError("Network error fetching clientSecret");
    }
  };
  if (user && isAdmin) fetchSecret();
}, [user, isAdmin]);
return (
  <main className="p-6 max-w-xl mx-auto space-y-4">
    <h1 className="text-xl font-semibold">Stripe Test PaymentElement</h1>

    {roleLoading && <div>Checking access…</div>}
    {!roleLoading && !user && (
      <div className="space-y-2">
        <div className="text-red-600">You must be signed in to access this page.</div>
        <a href="/auth"><Button>Go to Sign In</Button></a>
      </div>
    )}
    {!roleLoading && user && !isAdmin && (
      <div className="text-red-600">Forbidden: Admins only.</div>
    )}

    {error && <div className="text-red-600">Error: {error}</div>}
    {stripeKeyError && <div className="text-red-600">Error: {stripeKeyError}</div>}
    {user && isAdmin && (!clientSecret || !stripePromise) && <div>Loading Stripe...</div>}

    {user && isAdmin && clientSecret && stripePromise && (
      <Elements stripe={stripePromise} options={{ clientSecret }} key={clientSecret}>
        <CheckoutForm clientSecret={clientSecret} />
      </Elements>
    )}
  </main>
);
}
